import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { API } from "../../config";

interface Category {
  course_category_id: number;
  category_name: string;
  slug?: string | null;
  display_order: number;
  record_status_id: number;
}

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetch(`${API}/course_categories`)
      .then((r) => r.json())
      .then((data) => {
        const active = (data.categories as Category[])
          .filter((c) => c.record_status_id === 1 && !["training", "training for schools"].includes(c.category_name.trim().toLowerCase()))
          .sort((a, b) => a.display_order - b.display_order);
        setCategories(active);
      })
      .catch(() => {});
  }, []);

  const legacyRoutes: Record<string, string> = {
    "phonics": "/Phonics",
    "grammar & writing": "/GrammarWriting",
    "grammer & writing": "/GrammarWriting",
    "cambridge english": "/CambridgeEnglish",
    "spoken english": "/SpokenEnglish",
    "certification": "/Certification",
    "creative": "/Creative",
    "training": "/training-school",
    "training for schools": "/training-school",
  };

  const getCategoryPath = (cat: Category): string => {
    if (cat.slug) {
      const clean = cat.slug.replace(/^\//, "").trim();
      return `/${clean}`;
    }
    const legacy = legacyRoutes[cat.category_name.trim().toLowerCase()];
    if (legacy) return legacy;
    return `/ViewAllCourse?category=${encodeURIComponent(cat.category_name.trim())}`;
  };

  const goToCategory = (cat: Category) => {
    navigate(getCategoryPath(cat));
    setIsOpen(false);
  };

  const isCategoryActive = (cat: Category): boolean => {
    const path = getCategoryPath(cat);
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-red-600 shadow-sm w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* DESKTOP NAVBAR */}
        <div className="h-20 flex items-center justify-between">

          {/* LOGO */}
          <Link to="/" className="flex items-center">
            <img
              src="/images/phonicsLogo.webp"
              alt="Phonics Power"
              width={1596}
              height={612}
              decoding="async"
              className="logo-hw object-contain"
            />
          </Link>

          {/* NAVIGATION */}
          <nav className="hidden md:flex items-center gap-8">
            <NavLink
              to="/"
              className={({ isActive }) =>
                isActive
                  ? "text-sm font-semibold text-red-600"
                  : "header-menu text-gray-700 hover:text-red-600 transition"
              }
            >
              Home
            </NavLink>

            {categories.map((cat) => (
              <button
                key={cat.course_category_id}
                onClick={() => goToCategory(cat)}
                className={`text-sm font-medium transition ${
                  isCategoryActive(cat)
                    ? "text-red-600 border-b-2 border-red-600"
                    : "header-menu hover:text-red-600 border-b-2 border-transparent"
                }`}
              >
                {cat.category_name.trim()}
              </button>
            ))}

            <NavLink
              to="/centres"
              className="ml-4 inline-flex items-center justify-center rounded-md bg-red-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 transition"
            >
              Centres
            </NavLink>
          </nav>

          {/* RIGHT LOGO */}
          <div className="md:flex items-center">
            <img
              src="/images/cambridge_Logo.webp"
              alt="Cambridge English"
              width={2949}
              height={1301}
              decoding="async"
              className="h-14 w-auto object-contain"
            />
          </div>

          {/* MOBILE BUTTON */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden inline-flex items-center justify-center rounded-md p-2 hover:bg-gray-100 transition"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
          >
            {isOpen ? (
              <span className="text-2xl" aria-hidden="true">✕</span>
            ) : (
              <span className="text-2xl" aria-hidden="true">☰</span>
            )}
          </button>

        </div>

        {/* MOBILE MENU */}
        {isOpen && (
          <div className="md:hidden border-t border-gray-200 pb-4 pt-4">
            <nav className="flex flex-col gap-2">

              <NavLink to="/" onClick={() => setIsOpen(false)}
                className={({ isActive }) => isActive
                  ? "px-4 py-3 rounded-md text-sm font-semibold text-red-600 bg-red-50"
                  : "px-4 py-3 rounded-md text-sm text-gray-700 hover:bg-gray-50 hover:text-red-600"}>
                Home
              </NavLink>

              {categories.map((cat) => (
                <button
                  key={cat.course_category_id}
                  onClick={() => goToCategory(cat)}
                  className={`px-4 py-3 rounded-md text-sm text-left ${isCategoryActive(cat) ? "text-red-600 bg-red-50 font-semibold" : "text-gray-700 hover:bg-gray-50 hover:text-red-600"}`}
                >
                  {cat.category_name.trim()}
                </button>
              ))}

              <NavLink to="/centres" onClick={() => setIsOpen(false)}
                className="mx-4 mt-2 rounded-md bg-red-600 px-4 py-3 text-center text-sm font-semibold text-white hover:bg-red-700">
                Centres
              </NavLink>

            </nav>
          </div>
        )}

      </div>
    </header>
  );
}
